// Adapted from tinykeys: https://github.com/jamiebuilds/tinykeys/blob/main/src/tinykeys.ts
import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react"

/**
 * A single press of a keybinding sequence.
 */
export type KeyBindingPress = [mods: string[], key: string | RegExp]

/**
 * A map of keybinding strings to event handlers.
 */
export interface KeyBindingMap {
  [keybinding: string]: (event: KeyboardEvent) => void
}

/**
 * Options for sequence matching behavior.
 */
export interface KeyBindingHandlerOptions {
  /**
   * Keybinding sequences will wait this long between key presses before
   * cancelling (default: 1000).
   *
   * Note: Setting this too low will be too fast for many users.
   */
  timeout?: number
}

/**
 * Options to configure the behavior of the React keybindings subscription.
 */
export interface UseKeybindingsOptions extends KeyBindingHandlerOptions {
  /**
   * Key presses will listen on this event target (default: document).
   */
  target?: Window | Document | HTMLElement | null
  /**
   * Key presses will listen to this event (default: "keydown").
   */
  event?: "keydown" | "keyup"
  /**
   * Key presses will use a capture listener (default: false).
   */
  capture?: boolean
  /**
   * Enables or disables the listener without changing the binding map.
   */
  enabled?: boolean
  /**
   * Ignore key presses that originate from editable elements such as inputs,
   * textareas, selects, or contenteditable regions (default: true).
   */
  ignoreEditableTarget?: boolean
}

type ParsedKeyBinding = readonly [
  id: string,
  sequence: KeyBindingPress[],
  callback: (event: KeyboardEvent) => void,
]

/**
 * These are the modifier keys that change the meaning of keybindings.
 *
 * Note: Ignoring "AltGraph" because it is covered by the others.
 */
const KEYBINDING_MODIFIER_KEYS = ["Shift", "Meta", "Alt", "Control"]

/**
 * Keybinding sequences should timeout if individual key presses are more than
 * 1s apart by default.
 */
const DEFAULT_TIMEOUT = 1000

/**
 * Keybinding sequences should bind to this event by default.
 */
const DEFAULT_EVENT = "keydown" as const

/**
 * Platform detection code.
 * @see https://github.com/jamiebuilds/tinykeys/issues/184
 */
const PLATFORM = typeof navigator === "object" ? navigator.platform : ""
const APPLE_DEVICE = /Mac|iPod|iPhone|iPad/.test(PLATFORM)

/**
 * An alias for creating platform-specific keybinding aliases.
 */
const MOD = APPLE_DEVICE ? "Meta" : "Control"

/**
 * Meaning of `AltGraph`, from MDN:
 * - Windows: Both Alt and Ctrl keys are pressed, or AltGr key is pressed
 * - Mac: Option key pressed
 * - Linux: Level 3 Shift key (or Level 5 Shift key) pressed
 * - Android: Not supported
 * @see https://github.com/jamiebuilds/tinykeys/issues/185
 */
const ALT_GRAPH_ALIASES = PLATFORM === "Win32" ? ["Control", "Alt"] : APPLE_DEVICE ? ["Alt"] : []

/**
 * There's a bug in Chrome that causes event.getModifierState not to exist on
 * KeyboardEvents for F1/F2/etc keys.
 */
function getModifierState(event: KeyboardEvent, mod: string) {
  if (typeof event.getModifierState !== "function") {
    return false
  }

  return (
    event.getModifierState(mod) ||
    (ALT_GRAPH_ALIASES.includes(mod) && event.getModifierState("AltGraph"))
  )
}

/**
 * Parses a keybinding string into its component presses.
 *
 * grammar    = `<sequence>`
 * <sequence> = `<press> <press> <press> ...`
 * <press>    = `<key>` or `<mods>+<key>`
 * <mods>     = `<mod>+<mod>+...`
 * <key>      = `<KeyboardEvent.key>` or `<KeyboardEvent.code>` (case-insensitive)
 * <key>      = `(<regex>)` -> `/^<regex>$/` (case-sensitive)
 */
export function parseKeybinding(str: string): KeyBindingPress[] {
  return str
    .trim()
    .split(" ")
    .map((press) => {
      let mods = press.split(/\b\+/)
      let key: string | RegExp = mods.pop() as string
      const match = key.match(/^\((.+)\)$/)

      if (match) {
        key = new RegExp(`^${match[1]}$`)
      }

      mods = mods.map((mod) => (mod === "$mod" ? MOD : mod))

      return [mods, key]
    })
}

/**
 * Tells us if a single keyboard event matches a single keybinding press.
 */
function matchKeyBindingPress(event: KeyboardEvent, [mods, key]: KeyBindingPress): boolean {
  return !(
    (key instanceof RegExp
      ? !(key.test(event.key) || key.test(event.code))
      : key.toUpperCase() !== event.key.toUpperCase() && key !== event.code) ||
    mods.find((mod) => !getModifierState(event, mod)) ||
    KEYBINDING_MODIFIER_KEYS.find(
      (mod) => !mods.includes(mod) && key !== mod && getModifierState(event, mod),
    )
  )
}

type KeyboardShortcutEvent = KeyboardEvent | ReactKeyboardEvent<Element>

function getNativeKeyboardEvent(event: KeyboardShortcutEvent): KeyboardEvent {
  return "nativeEvent" in event ? event.nativeEvent : event
}

function isComposingKeyboardEvent(event: KeyboardShortcutEvent): boolean {
  return getNativeKeyboardEvent(event).isComposing || ("isComposing" in event && event.isComposing)
}

/**
 * Matches a single keybinding against an existing keyboard event.
 */
export function isKeyboardShortcut(event: KeyboardShortcutEvent, shortcut: string): boolean {
  const sequence = parseKeybinding(shortcut)
  if (sequence.length !== 1) {
    throw new Error(`isKeyboardShortcut only supports single-press shortcuts: ${shortcut}`)
  }

  if (isComposingKeyboardEvent(event)) return false

  return matchKeyBindingPress(getNativeKeyboardEvent(event), sequence[0])
}

/**
 * Creates an event listener for handling keybinding sequences.
 */
export function createKeybindingsHandler(
  getKeyBindings: () => readonly ParsedKeyBinding[],
  options: KeyBindingHandlerOptions = {},
): EventListener & { destroy: () => void } {
  const timeout = options.timeout ?? DEFAULT_TIMEOUT
  const possibleMatches = new Map<string, KeyBindingPress[]>()
  let timer: number | null = null

  const handler: EventListener & { destroy?: () => void } = (event) => {
    // Autocomplete option navigation and selection can fire a plain Event
    // instead of the expected KeyboardEvent.
    if (!(event instanceof KeyboardEvent)) {
      return
    }

    for (const [id, sequence, callback] of getKeyBindings()) {
      const remainingExpectedPresses = possibleMatches.get(id) ?? sequence
      const currentExpectedPress = remainingExpectedPresses[0]
      const matches = currentExpectedPress
        ? matchKeyBindingPress(event, currentExpectedPress)
        : false

      if (!matches) {
        // Modifier keydown events shouldn't break an in-progress sequence.
        if (!getModifierState(event, event.key)) {
          possibleMatches.delete(id)
        }
        continue
      }

      if (remainingExpectedPresses.length > 1) {
        possibleMatches.set(id, remainingExpectedPresses.slice(1))
        continue
      }

      possibleMatches.delete(id)
      callback(event)
    }

    if (timer) {
      clearTimeout(timer)
    }

    timer = window.setTimeout(() => {
      possibleMatches.clear()
    }, timeout)
  }

  handler.destroy = () => {
    possibleMatches.clear()

    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  return handler as EventListener & { destroy: () => void }
}

/**
 * Returns whether the event target is an editable element that should normally
 * ignore global keybindings.
 */
export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  return !!target.closest("input, textarea, select, [contenteditable='true']")
}

/**
 * Subscribes to keybindings and keeps the latest binding map across rerenders.
 *
 * Returns nothing; the listener is bound and cleaned up with React effects.
 */
export function useKeybindings(
  keyBindingMap: KeyBindingMap,
  {
    target,
    event = DEFAULT_EVENT,
    capture,
    timeout,
    enabled = true,
    ignoreEditableTarget = true,
  }: UseKeybindingsOptions = {},
) {
  const keyBindingsRef = useRef<ParsedKeyBinding[]>([])

  useEffect(() => {
    keyBindingsRef.current = Object.keys(keyBindingMap).map((keybinding) => {
      return [keybinding, parseKeybinding(keybinding), keyBindingMap[keybinding]] as const
    })
  }, [keyBindingMap])

  useEffect(() => {
    const eventTarget = target ?? (typeof document === "undefined" ? null : document)

    if (!enabled || !eventTarget) {
      return
    }

    const keyBindingsHandler = createKeybindingsHandler(() => keyBindingsRef.current, { timeout })
    const onKeyEvent: EventListener = (nativeEvent) => {
      if (ignoreEditableTarget && nativeEvent instanceof KeyboardEvent) {
        if (isEditableKeyboardTarget(nativeEvent.target)) {
          return
        }
      }

      keyBindingsHandler(nativeEvent)
    }

    eventTarget.addEventListener(event, onKeyEvent, capture)

    return () => {
      eventTarget.removeEventListener(event, onKeyEvent, capture)
      keyBindingsHandler.destroy()
    }
  }, [capture, enabled, event, ignoreEditableTarget, target, timeout])
}
